import { IComponent, IComponentChild, IComponentDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import React from "react";
import { ComponentService } from "../../../../services/component-service";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./component-def-fifth-step.scss";

export interface IComponentFormFifthStepState {
  childNames: string[];
  cSetId: string;
  selectedChildren: IComponentChild[];
  lastItemName: string;
}

export interface IComponentFormFifthStepProps {
  component: IComponent;
  componentSetId: string;
  selectedChildren: IComponentChild[];
  getInputChangeFromFifthStep: any;
}

export class ComponentFormFifthStep extends React.PureComponent<
  IComponentFormFifthStepProps,
  IComponentFormFifthStepState
> {
  constructor(props) {
    super(props);
    this.state = {
      childNames: [],
      cSetId: "",
      selectedChildren: [],
      lastItemName: ""
    };
  }

  // UNSAFE_componentWillReceiveProps = (nextProps) => {
  //   if (nextProps && nextProps.componentSetId && nextProps.componentSetId !== this.state.cSetId) {
  //     this.setState({
  //       cSetId: nextProps.componentSetId,
  //       selectedChildren: nextProps.selectedChildren
  //     });
  //     this.loadAllComponentsByComponentSetId(nextProps.componentSetId);
  //   }
  // };

  componentDidMount = async () => {
    await this.loadAllComponentsByComponentSetId();
    this.setState({
      cSetId: this.props.componentSetId,
      selectedChildren: this.props.selectedChildren
    });
  };

  async loadAllComponentsByComponentSetId() {
    try {
      const response = await ComponentService.GetAllByComponentSetId(this.props.componentSetId);
      const childNames: string[] = [];
      if (response.status === 200) {
        const components = response.data as IComponentDto[];
        components.forEach((c) => {
          const jsonObj = JSON.parse(c.json) as IComponent;
          if (jsonObj && jsonObj.isItChildItem === true) {
            if (childNames.indexOf(jsonObj.name) === -1) {
              childNames.push(jsonObj.name);
            }
          }
        });
      }
      if (childNames.length > 0) {
        this.setState({ childNames });
      }
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
    }
  }

  handleChange = (value: string[]) => {
    const result = [] as any;
    for (let i = 0; i < value.length; i++) {
      const name = value[i];
      const count = 0;
      const obj = { name, count };
      result.push(obj);
    }

    const childrenNamesType: IComponentChild[] = result;
    // childrenNamesType.push({name: value[value.length-1], count: 0});
    this.setState({ selectedChildren: [...childrenNamesType] });
  };

  handleInputChange = (e) => {
    this.props.getInputChangeFromFifthStep(e.target.value, e.target.name);
  };

  componentDidUpdate() {
    if (this.props.component?.childrenInfo?.length) {
      if (this.state.selectedChildren.length > this.props.component.childrenInfo.length) {
        const copyState = [...this.state.selectedChildren];
        const lastItem = copyState.slice(-1)[0];
        this.setState({ lastItemName: lastItem.name });
      }
    }
  }

  renderHint = (message: string): React.ReactNode => {
    return (
      <KuikaTooltip content={message}>
        <span className="kuika-child-step__hint">
          <KuikaSvg name="info-circle" width={14} height={14} color="var(--dark-6)" />
        </span>
      </KuikaTooltip>
    );
  };

  getChildOptions = (): KuikaSelectboxOption[] => {
    return this.state.childNames.map((name: string): KuikaSelectboxOption => ({ value: name, label: name }));
  };

  render() {
    const selectedChildren: IComponentChild[] = this.state.selectedChildren ?? [];
    return (
      <div className="kuika-child-step" data-testid="componentChildStep">
        <section className="kuika-child-step__section">
          <div className="kuika-child-step__label">
            <KuikaSmallBoldText className="kuika-child-step__section-title">Accepted children</KuikaSmallBoldText>
            {this.renderHint("The children this component can accept when it is dropped on the canvas.")}
          </div>
          {/* `ChildrenGroup` carries no value of its own; it is the labelled shell
              the wire field `childrenNames` lives in. */}
          <Form.Item name="childrenNames" noStyle>
            <KuikaSelectbox
              data-testid="componentChildrenSelect"
              mode="tags"
              searchable
              placeholder="Select Children"
              options={this.getChildOptions()}
              onChange={(value) => this.handleChange(value as string[])}
            />
          </Form.Item>
        </section>

        {selectedChildren.length === 0 ? (
          <KuikaEmptyState
            svgName="components"
            customName="No children selected"
            description="Pick one or more child components above to set how many are added by default."
            hideDescription={false}
          />
        ) : (
          <section className="kuika-child-step__section">
            <KuikaSmallBoldText className="kuika-child-step__section-title">Defaults per child</KuikaSmallBoldText>
            <div className="kuika-child-step__rows">
              {selectedChildren.map((item: IComponentChild) => (
                <div className="kuika-child-step__row" key={item.name} data-testid={`componentChildRow-${item.name}`}>
                  <div className="kuika-child-step__row-name">
                    <KuikaSmallText>{item.name}</KuikaSmallText>
                  </div>

                  <div className="kuika-child-step__row-control">
                    <div className="kuika-child-step__label">
                      <KuikaSmallText className="kuika-child-step__row-caption">Default count</KuikaSmallText>
                      {this.renderHint(`How many ${item.name} are created with this component at design time.`)}
                    </div>
                    <Form.Item
                      name={`set${item.name}`}
                      noStyle
                      // The child list loads after this step mounts, so without an
                      // initial value the input registers as uncontrolled and flips
                      // the moment the count arrives.
                      initialValue={item.count ?? 0}
                      rules={[
                        {
                          required: item.name === this.state.lastItemName,
                          message: "Please choose a child count."
                        }
                      ]}
                    >
                      <KuikaInput
                        data-testid={`componentChildCountInput-${item.name}`}
                        type="number"
                        min={0}
                        max={10}
                        onChange={(_e, value) => this.props.getInputChangeFromFifthStep(value, item.name)}
                      />
                    </Form.Item>
                  </div>

                  <div className="kuika-child-step__row-control kuika-child-step__row-control--fixed">
                    <div className="kuika-child-step__label">
                      <KuikaSmallText className="kuika-child-step__row-caption">Fixed</KuikaSmallText>
                      {this.renderHint("If on, this child cannot be removed at design time.")}
                    </div>
                    <Form.Item valuePropName="checked" name={`fix${item.name}`} noStyle>
                      <KuikaSwitch dataTestId={`componentChildFixedSwitch-${item.name}`} labelPosition="none" />
                    </Form.Item>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  }
}
